import React from 'react';
import { Field } from 'react-final-form';
import unionBy from 'lodash/unionBy';
import isEqual from 'lodash/isEqual';

import { showErrorOnBlurOrDirty, AutocompleteOption } from '@occc-app/design-system';
import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';

import {
  ILazyAutocompleteFieldProps,
  AutocompleteOptionType,
  ILazyAutocompletePropsNotMultiple,
} from './lazy-autocomplete.types';
import {
  mapAutocompleteOptionToRawResponseItem,
  useLazyAutocompleteLogic,
} from './lazy-autocomplete.utils';

import { Autocomplete } from '../autocomplete/autocomplete.component';

export function LazyAutocompleteNotMultiple<T = any, R = any>({
  value,
  errorText,
  useLazyQuery,
  makeQueryFilter,
  mapRawResponseItemToAutocompleteOption,
  mapQueryResponseToRawArray,
  onChange,
  ...props
}: ILazyAutocompletePropsNotMultiple<T, R>) {
  const {
    loading,
    renderInput,
    inputValue,
    onInputChange,
    responseOptionsArray,
    hasFilterOptions,
  } = useLazyAutocompleteLogic({
    ...props,
    errorText,
    useLazyQuery,
    makeQueryFilter,
    mapQueryResponseToRawArray,
    mapRawResponseItemToAutocompleteOption,
  });

  const { isEditable } = useCanEditFormFieldsContext();

  const autocompleteValue = React.useMemo(
    () => (value ? mapRawResponseItemToAutocompleteOption(value) : null),
    [value, mapRawResponseItemToAutocompleteOption],
  );

  const options: AutocompleteOptionType[] = React.useMemo(
    () => unionBy(responseOptionsArray, autocompleteValue && [autocompleteValue], 'value'),
    [responseOptionsArray, autocompleteValue],
  );

  const onChangeMemo = React.useCallback(
    (event: any, newValue: AutocompleteOptionType | null) => {
      if (!newValue) {
        onChange(null);
        return;
      }
      onChange(mapAutocompleteOptionToRawResponseItem(newValue));
    },
    [onChange],
  );

  const getOptionSelected = React.useCallback(
    (option: AutocompleteOption, currentValue: AutocompleteOption) => {
      // Needed to cover initialValue as string
      return currentValue !== null && option.value === currentValue.value;
    },
    [],
  );

  const disabled = props.loading || props.disabled || !isEditable;

  return (
    <Autocomplete
      {...props}
      // TODO: it's boolean but library types are broken
      disableClearable={!value as any}
      multiple={false}
      inputValue={inputValue}
      onInputChange={onInputChange}
      value={autocompleteValue}
      onChange={onChangeMemo}
      options={options}
      isOptionEqualToValue={getOptionSelected}
      filterOptions={hasFilterOptions ? props.filterOptions : undefined}
      renderInput={renderInput}
      disabled={disabled}
      loading={props.loading || loading}
    />
  );
}

export function LazyAutocompleteFieldNotMultiple<T = any, R = any>(
  props: ILazyAutocompleteFieldProps<T, R>,
) {
  const renderInput = ({ input, meta }: any) => {
    const isError = showErrorOnBlurOrDirty({ meta });
    return (
      <LazyAutocompleteNotMultiple<T, R>
        {...props}
        errorText={isError ? meta.error : ''}
        value={input.value || null}
        onChange={(value) => {
          props.onChange?.(value);
          return input.onChange(value);
        }}
        onBlur={input.onBlur}
        onFocus={input.onFocus}
      />
    );
  };

  return <Field name={props.name} isEqual={isEqual} render={renderInput} />;
}
